import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/crm/management/brands/locations';
export const getCrmBrandLocationsEndpointIdentifier = endpoint;

export type ICrmBrandLocationData = {
  id: number;
  name: string;
  address: string;
  area: string;
  type: 'physical_location' | 'cloud_kitchen';
  landlineNumber: string;
  brand: {
    id: number;
    name: string;
    image: string;
  };
  geoLocation: {
    id: number;
    name: string;
  } | null;
};

type TData = {
  pageCount: number;
  total: number;
  data: ICrmBrandLocationData[];
};

type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    sort: TParam;
    name: TParam;
    brandId: TParam;
  }
];

type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetCrmBrandLocationsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { sort, name, brandId } = getPageParams();
  const { ...otherOptions } = options || {};

  const queryFn = async () => {
    try {
      const params: any = {
        page,
        limit,
        sort,
      };

      if (name) {
        params.name = name;
      }

      if (brandId) {
        params.brandId = brandId;
      }

      const { data } = await http.get(endpoint, {
        params,
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      endpoint,
      {
        page,
        limit,
        sort,
        name,
        brandId,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn,
      ...otherOptions,
    }
  );
}
